import {
  CreateBookingV3Corporate,
  CreateBookingV3Retail
} from "@/data/payload/api/BookingRequest";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { createI18n } from "vue-i18n";
import { AccountController } from "@/app/ui/controllers/AccountController";
import formatPriceRP from "@/app/infrastructures/misc/common-library/v2/FormatPriceRP";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

const i18nInstance = createI18n({
  legacy: false,
  locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
  fallbackLocale: "id",
  globalInjection: false,
  messages: {
    en,
    id,
    my
  }
});

const translate = i18nInstance.global.t;

const payloadBookingManual = (payload: any) => {
  return new CreateBookingV3Retail({
    sttManual: payload.sttManual,
    referenceExternal: payload.referenceExternal,
    productName: payload.productName,
    originCity: payload.originCity,
    originDistrict: payload.originDistrict,
    destinationCity: payload.destinationCity,
    destinationDistrict: payload.destinationDistrict,
    phoneSender: payload.phoneSender,
    phoneReceiver: payload.phoneReceiver,
    sender: payload.sender,
    receiver: payload.receiver,
    addressSender: payload.addressSender,
    addressReceiver: payload.addressReceiver,
    addressReceiverType: payload.addressReceiverType,
    sttGrossWeight: payload.sttGrossWeight,
    sttVolumeWeight: payload.sttVolumeWeight,
    sttChargeableWeight: payload.sttChargeableWeight,
    commodityCode: payload.commodityCode,
    insurance: payload.insurance,
    statusGoods: payload.statusGoods,
    estimationPrice: payload.estimationPrice,
    taxNumber: payload.taxNumber,
    isWoodpack: payload.isWoodpack,
    isCOD: payload.isCOD,
    isDFOD: payload.isDFOD,
    sttAttachFiles: payload.sttAttachFiles,
    sttCommodityDetail: payload.sttCommodityDetail,
    isSaveSender: payload.isSaveSender,
    isSaveReceiver: payload.isSaveReceiver,
    sttPieces: payload.sttPieces,
    isDO: payload.isDO,
    isMixpack: payload.isMixpack,
    sttPiecePerPack: payload.sttPiecePerPack,
    sttNextCommodity: payload.sttNextCommodity,
    sttCodAmount: payload.codAmount,
    postalCodeDestination: payload.postalCodeDestination,
    isPAD: payload.isPAD,
    sttWeightAttachFiles: payload.sttWeightAttachFiles,
    sttRecipientEmail: payload.sttRecipientEmail,
    sttKtpImage: payload.sttKtpImage,
    sttTaxImage: payload.sttTaxImage,
    sttIdentityNumber: payload.sttIdentityNumber,
    sttInterTaxNumber: payload.sttInterTaxNumber,
    sttCipl: payload.sttCipl || [],
    bulkId: payload.bulkId,
    bulkSttRowNumber: payload.bulkSttRowNumber,
    sttShipmentId: payload.sttShipmentId,
    sttDutyTaxTerms: payload.sttDutyTaxTerms
  });
};

const payloadBookingCorporate = (
  payload: any,
  statusGoodValueForclients: string,
  insuranceAsDefault: string
) => {
  return new CreateBookingV3Corporate({
    sttClient: payload.clientName.id || payload.clientId,
    sttManual: payload.sttManual,
    sttShipmentId: payload.sttShipmentId,
    referenceExternal: payload.referenceExternal,
    taxNumber: payload.taxNumber,
    estimationPrice: payload.estimationPrice,
    statusGoods: payload.statusGoods || statusGoodValueForclients,
    originCity: payload.originCity,
    originDistrict: payload.originDistrict,
    destinationCity: payload.destinationCity,
    destinationDistrict: payload.destinationDistrict,
    sender: payload.sender,
    phoneSender: payload.phoneSender,
    addressSender: payload.addressSender,
    receiver: payload.receiver,
    addressReceiver: payload.addressReceiver,
    addressReceiverType: payload.addressReceiverType,
    phoneReceiver: payload.phoneReceiver,
    insurance: payload.insurance || insuranceAsDefault,
    productName: payload.productName,
    commodityCode: payload.commodityCode,
    isCOD: payload.isCODClient,
    isDFOD: payload.isDFODClient,
    isPAD: payload.isPAD,
    isWoodpack: payload.isWoodpack,
    sttPieces: payload.sttPieces,
    sttPiecePerPack: payload.sttPiecePerPack,
    sttNextCommodity: payload.sttNextCommodity,
    sttCodAmount: payload.codAmount,
    postalCodeDestination: payload.postalCodeDestination,
    sttWeightAttachFiles: payload.sttWeightAttachFiles,
    sttRecipientEmail: payload.sttRecipientEmail,
    sttKtpImage: payload.sttKtpImage,
    sttTaxImage: payload.sttTaxImage,
    sttCommodityDetail: payload.sttCommodityDetail,
    sttAttachFiles: payload.sttAttachFiles,
    sttIdentityNumber: payload.sttIdentityNumber,
    sttInterTaxNumber: payload.sttInterTaxNumber,
    sttCipl: payload.sttCipl || [],
    bulkId: payload.bulkId,
    bulkSttRowNumber: payload.bulkSttRowNumber,
    sttDutyTaxTerms: payload.sttDutyTaxTerms
  });
};

const generateSttLabelElement = (res: ResponsePayload) => {
  const isCod = res.data.is_cod && !res.data.is_dfod;
  const isDfod = res.data.is_cod && res.data.is_dfod;

  if (isCod) {
    return `
  <div class="flex flex-row justify-center items-center pb-6 text-center border-b border-gray-lp-2000 relative gap-2">
    <img class="invisible" src="${require("@/app/ui/assets/svg/cod-logo-2.svg")}" />
    <h1 class="text-2xl underline font-bold text-red-lp-100 w-max">${
      res.sttNumber
    }</h1>
    <div class="rounded-lg border border-gray-lp-2100 bg-gray-lp-100 p-2">
      <img src="${require("@/app/ui/assets/svg/cod-logo-2.svg")}" />
    </div>
  </div>
`;
  }

  if (isDfod) {
    return `
  <div class="flex flex-row justify-center items-center pb-6 text-center border-b border-gray-lp-2000 relative gap-2">
    <img class="invisible" src="${require("@/app/ui/assets/svg/dfod-logo-2.svg")}" />
    <h1 class="text-2xl underline font-bold text-red-lp-100 w-max">${
      res.sttNumber
    }</h1>
    <div class="rounded-lg border border-gray-lp-2100 bg-gray-lp-100 p-2">
      <img src="${require("@/app/ui/assets/svg/dfod-logo-2.svg")}" />
    </div>
  </div>
`;
  }

  return `
  <div class="flex flex-row justify-center items-center pb-6 text-center border-b border-gray-lp-2000 relative gap-2">
    <h1 class="text-2xl underline font-bold text-red-lp-100 w-max">${res.sttNumber}</h1>
  </div>
`;
};

const generateDiscountElement = (res: ResponsePayload) => {
  if (!res.data.list_discount.length) return "";

  return (
    res.data.list_discount.map((discount: any, index: number) => {
      return `
<div class="flex flex-row items-center gap-1 px-1 py-0.5 rounded bg-green-lp-700 text-white text-10px">
  ${discount.config.pdc_promo_discount}% Off
  <img src="${require("@/app/ui/assets/svg/information-circle-green-1.svg")}" />
</div>
${
  index !== res.data.list_discount.length - 1
    ? '<div class="text-base font-semibold text-gray-lp-600">+</div>'
    : ""
}
`;
    }) +
    `
<div class="text-sm text-gray-lp-600">
  <s>${formatPriceRP(
    res.totalBeforeDiscount,
    AccountController.accountData.accountCurrency
  )}</s>
</div>
`
  );
};

const checkIsClient = (isClient = false, isClientAccount = false) => {
  return isClient || isClientAccount;
};

const generateTotalTariffElement = (
  res: ResponsePayload,
  isClient = false,
  isClientAccount = false
) => {
  const isCod = res.data.is_cod;

  const formatPrice = (value: any, currency: "IDR" | "MYR" = "IDR") => {
    if (res.data?.isCrossCountryCod) {
      const formatted = formatByCountry(value, {
        type: "weight",
        country: res.data?.destinationCountry.code,
        fractionDigits: 6,
        minFractionDigits: 0
      });

      return `${res.data?.prefixCurrency}${formatted}`;
    }

    return formatPriceRP(value, currency);
  };

  if (checkIsClient(isClient, isClientAccount) && isCod) {
    return `
<div class="flex flex-row justify-between items-center">
  <div class="text-gray-lp-300">${translate("bulkBooking.modal.codCost")}</div>
  <div class="flex flex-row gap-1 items-center">
      <div class="text-gray-lp-300 font-semibold">${
        res.data?.prefixCurrency ||
        AccountController.accountData.accountIsForeign
          ? "RM"
          : "Rp"
      }-</div>
  </div>
</div>
<div class="flex flex-row justify-between items-center">
  <div class="text-gray-lp-300">${translate("bulkBooking.modal.mustPaid")}</div>
  <div class="flex flex-row gap-1 items-center">
    <div class="text-gray-lp-300 font-semibold">${
      res.data?.prefixCurrency || AccountController.accountData.accountIsForeign
        ? "RM"
        : "Rp"
    }-</div>
  </div>
</div>

`;
  }

  if (isClient || isClientAccount) {
    return `
    <div class="flex flex-row justify-between items-center">
      <div class="text-gray-lp-300">${translate(
        "bulkBooking.modal.totalTariff"
      )}</div>
      <div class="flex flex-row gap-1 items-center">
        ${generateDiscountElement(res)}
        <div class="text-gray-lp-300 font-semibold">${
          res.data?.prefixCurrency ||
          AccountController.accountData.accountIsForeign
            ? "RM"
            : "Rp"
        }-</div>
      </div>
    </div>
  `;
  }

  if (isCod) {
    return `
  <div class="flex flex-row justify-between items-center">
    <div class="text-gray-lp-300">${translate(
      "bulkBooking.modal.codCost"
    )}</div>
    <div class="flex flex-row gap-1 items-center">
      <div class="text-gray-lp-300 font-semibold">${formatPrice(
        res.data.cod_fee,
        AccountController.accountData.accountCurrency
      )}</div>
    </div>
  </div>
  <div class="flex flex-row justify-between items-center">
    <div class="text-gray-lp-300">${translate(
      "bulkBooking.modal.mustPaid"
    )}</div>
    <div class="flex flex-row gap-1 items-center">
      <div class="text-gray-lp-300 font-semibold">${formatPrice(
        res.data.cod_amount,
        AccountController.accountData.accountCurrency
      )}</div>
    </div>
  </div>

`;
  }

  return `
  <div class="flex flex-row justify-between items-center">
    <div class="text-gray-lp-300">${translate(
      "bulkBooking.modal.totalTariff"
    )}</div>
    <div class="flex flex-row gap-1 items-center">
      ${generateDiscountElement(res)}
      <div class="text-gray-lp-300 font-semibold">${
        res.totalAfterDiscount
          ? formatPriceRP(
              res.totalAfterDiscount,
              AccountController.accountData.accountCurrency
            )
          : res.data?.prefixCurrency ||
            (AccountController.accountData.accountIsForeign ? "RM-" : "Rp-")
      }</div>
    </div>
  </div>
`;
};

const generateEstimationElement = (res: ResponsePayload) => {
  const locale = AccountController.accountData.countryCode.toLowerCase();
  const isMonthYearIsSame =
    formatDate(
      res.data.estimation_date.split(" - ")[0],
      "MMMM YYYY",
      true,
      locale,
      false
    ) ===
    formatDate(
      res.data.estimation_date.split(" - ")[1],
      "MMMM YYYY",
      true,
      locale,
      false
    );
  const estimationDateStart = formatDate(
    res.data.estimation_date.split(" - ")[0],
    `${isMonthYearIsSame ? "D" : "D MMMM YYYY"}`,
    true,
    locale,
    false
  );
  const estimationDateEnd = formatDate(
    res.data.estimation_date.split(" - ")[1],
    "D MMMM YYYY",
    true,
    locale,
    false
  );

  return `
    <div class="flex flex-row justify-between items-center">
      <div class="text-gray-lp-300">${translate(
        "bulkBooking.modal.deliveryEst"
      )}</div>
      <div class="text-gray-lp-800 font-semibold">${estimationDateStart} - ${estimationDateEnd}</div>
    </div>
`;
};

const generateMessageModalSuccessPopup = (
  res: ResponsePayload,
  productType: string,
  isClient = false,
  isClientAccount = false
) => {
  return `
<div class="flex flex-col gap-6 w-full">
  ${generateSttLabelElement(res)}
  <div class="flex flex-col gap-3 text-sm pb-6 border-b border-gray-lp-2000">
    ${generateEstimationElement(res)}
    <div class="flex flex-row justify-between items-center">
      <div class="text-gray-lp-300">${translate(
        "bulkBooking.modal.productType"
      )}</div>
      <div class="text-gray-lp-300 px-2 py-0.5 bg-gray-lp-200 rounded font-medium">${productType}</div>
    </div>
    <div class="flex flex-row justify-between items-baseline">
      <div class="text-gray-lp-300">${translate(
        "bulkBooking.modal.recipient"
      )}</div>
      <div class="text-gray-lp-800 font-semibold w-1/2 text-right">${
        res.data.recipient_name
      }</div>
    </div>
    <div class="flex flex-row justify-between items-baseline">
      <div class="text-gray-lp-300">${translate(
        "bulkBooking.modal.districtCity"
      )}</div>
      <div class="text-gray-lp-800 font-semibold w-1/2 text-right">${
        res.data.destination_district_name
      }, ${res.data.destination_city_name}</div>
    </div>
    ${generateTotalTariffElement(res, isClient, isClientAccount)}
  </div>
</div>
`;
};

export {
  payloadBookingManual,
  payloadBookingCorporate,
  generateSttLabelElement,
  generateDiscountElement,
  generateTotalTariffElement,
  generateEstimationElement,
  generateMessageModalSuccessPopup
};
